import { FleetProductType, normalizeProductType, rawProductType, isFleetProductType } from './fleetConstants';

export type FleetCollectionSet = {
  vehicles: string;
  trips: string;
  refuelEvents: string;
  issueReports: string;
  shipmentAssignments: string;
  orgField: 'schoolCode' | 'orgCode';
};

/** Resolve Firestore collections for fleet portal (v2 unified + legacy per org type). */
export function getFleetCollections(productType: string | undefined): FleetCollectionSet {
  // Use the RAW product type, not the normalized one: normalizeProductType()
  // collapses BUS_ONLY into SELF_TRACKING, which would make the LEGACY_BUS_ONLY
  // branch below unreachable and route bus-tracking orgs to the wrong
  // (fleetVehicles/orgCode) collections instead of buses/schoolCode.
  const pt = rawProductType(productType);
  if (pt === FleetProductType.CARGO || pt === FleetProductType.LEGACY_LORRY) {
    return {
      vehicles: 'lorries',
      trips: 'lorryTrips',
      refuelEvents: 'lorryRefuelEvents',
      issueReports: 'lorryIssueReports',
      shipmentAssignments: 'lorryAssignments',
      orgField: 'schoolCode',
    };
  }
  if (pt === FleetProductType.SELF_TRACKING) {
    return {
      vehicles: 'fleetVehicles',
      trips: 'fleetTrips',
      refuelEvents: 'fleetRefuelEvents',
      issueReports: 'busIssueReports',
      shipmentAssignments: 'lorryAssignments',
      orgField: 'orgCode',
    };
  }
  if (pt === FleetProductType.LEGACY_BUS_ONLY || pt === FleetProductType.FULL_FEATURE || !pt) {
    // School / Beta FULL_FEATURE portals load BusTracking but write to `buses` + schoolCode
    // (not `vehicles`/`orgCode`). Using the wrong set shows drivers as "No bus" and empty
    // parent children even when Firestore has correct data.
    return {
      vehicles: 'buses',
      trips: 'busAttendance',
      refuelEvents: 'busRefuelEvents',
      issueReports: 'busIssueReports',
      shipmentAssignments: 'lorryAssignments',
      orgField: 'schoolCode',
    };
  }
  return {
    vehicles: 'vehicles',
    trips: 'trips',
    refuelEvents: 'refuelEvents',
    issueReports: 'issueReports',
    shipmentAssignments: 'shipmentAssignments',
    orgField: 'orgCode',
  };
}

export function fleetHasFullProductFeatures(productType: string | undefined): boolean {
  return !isFleetProductType(productType);
}

export function fleetPortalTitle(productType: string | undefined): string {
  const pt = normalizeProductType(productType);
  if (pt === FleetProductType.CARGO) return 'Cargo fleet console';
  if (pt === FleetProductType.SELF_TRACKING) return 'Fleet console';
  return 'Fleet dashboard';
}

/** Self-tracking vs cargo portal nav items */
export function fleetNavItems(productType: string | undefined) {
  const pt = normalizeProductType(productType);
  const base = [
    { id: 'overview', label: 'Overview' },
    { id: 'live', label: 'Live map' },
  ];
  if (pt === FleetProductType.CARGO) {
    return [
      ...base,
      { id: 'cargoTrips', label: 'Cargo trips' },
      { id: 'vehicles', label: 'Vehicles' },
      { id: 'clients', label: 'Clients' },
      { id: 'fuel', label: 'Fuel logs' },
      { id: 'reports', label: 'Reports' },
      { id: 'settings', label: 'Settings' },
    ];
  }
  return [
    ...base,
    { id: 'vehicles', label: 'Vehicles' },
    { id: 'drivers', label: 'Drivers' },
    { id: 'fuel', label: 'Fuel logs' },
    { id: 'reports', label: 'Reports' },
    { id: 'settings', label: 'Settings' },
  ];
}
